import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Input from '@mui/material/Input';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

import Resizer from 'react-image-file-resizer';

import AuthService from '../services/auth';

import { Navigate, useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import apiClient, { getErrorMessage } from '../services/api-client';

const cardSx = {
  borderRadius: '16px',
  boxShadow: 0,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'background.paper',
};

const sectionTitleSx = {
  fontSize: 14,
  fontWeight: 800,
  color: 'text.primary',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

function NewReport({ setNavbarState, navbarState}) {
    const navigate = useNavigate();
    const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
    const [area, setArea] = useState('');
    const [descripcionBreve, setDescripcionBreve] = useState('');
    const [descripcionDetallada, setDescripcionDetallada] = useState('');
    const [image, setImage] = useState(null);
    const [showBackdrop, setShowBackdrop] = React.useState(false);
    const [categories, setCategories] = React.useState([]);


    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setSnackbar({ ...snackbar, open: false });
      };
    
    const handleAreaChange = (e) => {
        setArea(e.target.value);
    };

    const fetchCategories = async () => {
        try {
            const response = await apiClient.get('issue/categories', {}, { action: 'fetch_issue_categories' });
            if (response.status === 200) {
                setCategories(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }

    const handleDescripcionBreveChange = (e) => {
        setDescripcionBreve(e.target.value);
    };

    const handleDescripcionDetalladaChange = (e) => {
        setDescripcionDetallada(e.target.value);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file.size > 1024 * 1024) {
            Resizer.imageFileResizer(
            file,
            800, // Max width
            800, // Max height
            'JPEG', // Output format
            80, // Quality
            0, // Rotation
            (compressedFile) => {
                setImage(compressedFile);
                setShowBackdrop(false);
            },
            'blob' // Output type
            );
        } else {
            if (file.type.startsWith('image/')) {
            setImage(file);
            setShowBackdrop(false);
            } else {
            setShowBackdrop(false);
            setSnackbar({ open: true, message: 'Por favor, solo utilice imagenes', severity: 'error' });
            setImage(null);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (area.trim() === '' || descripcionBreve.trim() === '' || descripcionDetallada.trim() === '') {
            setSnackbar({ open: true, message: 'Todos los campos son requeridos', severity: 'error' });
            return;
        }

        setShowBackdrop(true);
        const formData = new FormData();
        formData.append('area', area);
        formData.append('descripcionBreve', descripcionBreve);
        formData.append('descripcionDetallada', descripcionDetallada);
        if (image) {
            formData.append('image', image);
        }
        
        try {
            const response = await apiClient.post('issue/create', formData, {}, { action: 'create_issue_report' });
            if (response.status === 200) {
                setRedirect({ trigger: true, route: '/reportes' });
            }
        } catch (error) {
            console.error('Error creating report:', error);
            setSnackbar({ open: true, message: getErrorMessage(error, "Hubo un error al intentar agregar tu reporte, porfavor intente mas tarde."), severity: 'error' });
        } finally {
            setShowBackdrop(false);
        }
    };

    React.useEffect(() => {
        setNavbarState({...navbarState, showBack: true, showOptions: false, showSearch: false});
        fetchCategories();
    },[]);

    return (
        <Container maxWidth="sm" disableGutters>
            {redirect.trigger && (
                <Navigate to={redirect.route} replace={true} />
            )}
            <Snackbar open={snackbar.open} autoHideDuration={8000} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                {snackbar.message}
                </Alert>
            </Snackbar>
            <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        px: 3,
                        pt: 2,
                        pb: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                width: 40,
                                height: 40,
                                ml: -1,
                                color: 'text.primary',
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 28 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
                            Reportar Avería
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* Header card */}
                    <Card sx={{ ...cardSx, overflow: 'visible' }}>
                        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                            <Avatar
                                sx={{
                                    width: 72,
                                    height: 72,
                                    bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                                    color: 'secondary.main',
                                }}
                            >
                                <ReportProblemOutlinedIcon sx={{ fontSize: 38 }} />
                            </Avatar>
                            <Box>
                                <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary', lineHeight: 1.2 }}>
                                    Nueva Avería
                                </Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mt: 0.25 }}>
                                    Completa los detalles del problema
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Card 2: Detalles del Reporte */}
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2.5}>
                                <Typography sx={sectionTitleSx}>
                                    <ReportProblemOutlinedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                                    Detalles del Reporte
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="medium"
                                    id="area"
                                    label="Área"
                                    variant="outlined"
                                    placeholder="Ej. 1er Nivel Torre 3"
                                    onChange={handleAreaChange}
                                />
                                <TextField
                                    fullWidth
                                    size="medium"
                                    id="shortdescript"
                                    label="¿Cuál es el problema?"
                                    variant="outlined"
                                    placeholder="Ej. Fuga de Agua"
                                    onChange={handleDescripcionBreveChange}
                                />
                                <TextField
                                    fullWidth
                                    size="medium"
                                    id="lomgdescript"
                                    label="Descripción detallada"
                                    variant="outlined"
                                    rows={4}
                                    multiline
                                    placeholder="Incluye todos los detalles para facilitar identificar el problema, Ej. Hay una fuga de agua al lado de la puerta de ingreso"
                                    onChange={handleDescripcionDetalladaChange}
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Card 3: Imagen (opcional) */}
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2.5}>
                                <Typography sx={sectionTitleSx}>
                                    <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                                    Imagen (opcional)
                                </Typography>

                                {image ? (
                                    <Box sx={{ position: 'relative' }}>
                                        <Box
                                            component="img"
                                            src={URL.createObjectURL(image)}
                                            alt="Selected Image"
                                            sx={{
                                                width: '100%',
                                                maxHeight: 280,
                                                objectFit: 'cover',
                                                borderRadius: '12px',
                                                display: 'block',
                                            }}
                                        />
                                        <IconButton
                                            onClick={() => setImage(null)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                width: 36,
                                                height: 36,
                                                bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
                                                color: 'common.white',
                                                '&:hover': { bgcolor: (theme) => alpha(theme.palette.common.black, 0.7) },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                        <label htmlFor="contained-button-file">
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                component="span"
                                                fullWidth
                                                startIcon={<PhotoCameraOutlinedIcon />}
                                                sx={{ mt: 2, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Cambiar Imagen
                                            </Button>
                                            <Input
                                                type="file"
                                                onChange={handleImageChange}
                                                id="contained-button-file"
                                                sx={{ display: 'none' }}
                                            />
                                        </label>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box
                                            sx={{
                                                border: '2px dashed',
                                                borderColor: 'divider',
                                                borderRadius: '12px',
                                                p: 3,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Avatar
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                                                    color: 'secondary.main',
                                                }}
                                            >
                                                <AddPhotoAlternateIcon sx={{ fontSize: 24 }} />
                                            </Avatar>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                                                Arrastra una imagen o pulsa para seleccionar
                                            </Typography>
                                            <label htmlFor="contained-button-file">
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    component="span"
                                                    startIcon={<PhotoCameraOutlinedIcon />}
                                                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                                                >
                                                    Agregar Imagen
                                                </Button>
                                                <Input
                                                    type="file"
                                                    onChange={handleImageChange}
                                                    id="contained-button-file"
                                                    sx={{ display: 'none' }}
                                                />
                                            </label>
                                        </Box>
                                    </Box>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    {/* Footer */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            px: 0.5,
                        }}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}>
                            Recuerda que todos los vecinos de {AuthService.getCurrentUserOrg()} pueden ver los reportes
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            color="secondary"
                            size="large"
                            onClick={handleSubmit}
                            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, py: 1.5 }}
                        >
                            Enviar Reporte
                        </Button>
                        <Button
                            variant="text"
                            color="primary"
                            size="large"
                            fullWidth
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                            onClick={() => setRedirect({ trigger: true, route: '/reportes' })}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
}

export default NewReport;
